import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Landmark } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";
import {
  saveBusinessSettings,
  formatReportingStartLabel,
} from "@/lib/businessSettings";

export default function BusinessSettingsCard() {
  const { settings, reload } = useBusinessSettings();
  const { toast } = useToast();
  const [startDate, setStartDate] = useState(settings.reportingStartDate);
  const [includeHistorical, setIncludeHistorical] = useState(settings.includeHistorical);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStartDate(settings.reportingStartDate);
    setIncludeHistorical(settings.includeHistorical);
  }, [settings.reportingStartDate, settings.includeHistorical]);

  const dirty =
    startDate !== settings.reportingStartDate ||
    includeHistorical !== settings.includeHistorical;

  const onSave = async () => {
    setSaving(true);
    try {
      await saveBusinessSettings({
        reportingStartDate: startDate,
        includeHistorical,
      });
      await reload();
      toast({ title: "Business settings saved" });
    } catch (err: any) {
      toast({
        title: "Could not save",
        description: err?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Landmark className="h-4 w-4" /> Business Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Silver Sage operations officially began on the date below. Every financial
          dashboard, projection, and MAVIS insight ignores transactions from before
          this date unless <span className="font-medium">Include historical data</span>{" "}
          is turned on.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="reporting-start-date">Financial Reporting Start Date</Label>
            <Input
              id="reporting-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Defaults to July 1, 2026 — when Silver Sage took ownership and the
              new Stripe account became active.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="block">Include historical data</Label>
            <div className="flex items-center gap-3 rounded-md border border-border bg-background/60 px-3 py-2">
              <Switch
                checked={includeHistorical}
                onCheckedChange={setIncludeHistorical}
                id="include-historical"
              />
              <div className="text-xs text-muted-foreground">
                When on, dashboards include every transaction on record, regardless of
                the start date above.
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-muted-foreground italic">
            {formatReportingStartLabel({
              reportingStartDate: startDate,
              includeHistorical,
              updatedAt: null,
            })}
          </p>
          <Button size="sm" onClick={onSave} disabled={!dirty || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
